import { useEffect } from 'react';
import { Card } from 'react-bootstrap';

import { Error, Loading } from '@components';
import { useGetChannelDailyTotalExpectedProfitQuery } from '@query/dashboard/hooks';
import { numberWithCommas } from '@utils';

import { applyExchageRate, generateBarChartData } from '../utils';

import { BarChart } from './BarChart';
import { DataTable } from './DataTable';
import { Tabs } from './Tabs';

type Props = {
  startAt: string;
  endAt: string;
  exchangeRate: string;
  isEnabled: boolean;
  onFetchComplete: () => void;
};

export function ChannelDailyTotalExpectedProfit({
  startAt,
  endAt,
  exchangeRate,
  isEnabled,
  onFetchComplete
}: Props) {
  const { data, isLoading, isFetching, isError, refetch } =
    useGetChannelDailyTotalExpectedProfitQuery({
      queryParams: {
        startAt,
        endAt,
        exchangeRate,
        orderBy: 'ASC'
      },
      options: {
        enabled: isEnabled,
        onSettled: onFetchComplete
      }
    });

  useEffect(() => {
    if (isEnabled) {
      refetch();
    }
  }, [isEnabled]);

  const makePreprocessedData = (
    responseData: NonNullable<typeof data>['channelTotalExpectedProfitList']
  ) => {
    return responseData.map(
      ({
        date,
        SUBSCRIPTION_PRODUCT_EXPECTED_PROFIT_KRW,
        SUBSCRIPTION_PRODUCT_EXPECTED_PROFIT_USD,
        P_APP_UNLOCK_PRODUCT_EXPECTED_PROFIT_KRW,
        P_APP_UNLOCK_PRODUCT_EXPECTED_PROFIT_USD,
        TOTAL_CP_EXPECTED_PROFIT
      }) => ({
        date,
        SUBSCRIPTION_PRODUCT_EXPECTED_PROFIT: String(
          Number(SUBSCRIPTION_PRODUCT_EXPECTED_PROFIT_KRW) +
            applyExchageRate(SUBSCRIPTION_PRODUCT_EXPECTED_PROFIT_USD, exchangeRate)
        ),
        P_APP_UNLOCK_PRODUCT_EXPECTED_PROFIT: String(
          Number(P_APP_UNLOCK_PRODUCT_EXPECTED_PROFIT_KRW) +
            applyExchageRate(P_APP_UNLOCK_PRODUCT_EXPECTED_PROFIT_USD, exchangeRate)
        ),
        TOTAL_CP_EXPECTED_PROFIT
      })
    );
  };

  return (
    <Card>
      <Card.Body>
        <Tabs
          title="일별 채널의 총 예상 기대이익"
          contentsHeight="400px"
          tabItems={[
            {
              id: 'BAR_CHART',
              title: 'Bar Chart',
              content: (
                <>
                  {isError ? (
                    <Error />
                  ) : isLoading || isFetching ? (
                    <Loading />
                  ) : (
                    <BarChart
                      data={generateBarChartData({
                        type: 'bar',
                        keys: [
                          'SUBSCRIPTION_PRODUCT_EXPECTED_PROFIT',
                          'P_APP_UNLOCK_PRODUCT_EXPECTED_PROFIT',
                          'TOTAL_CP_EXPECTED_PROFIT'
                        ],
                        labels: {
                          SUBSCRIPTION_PRODUCT_EXPECTED_PROFIT: '구독상품 기대이익 (B+C)',
                          P_APP_UNLOCK_PRODUCT_EXPECTED_PROFIT: '유료이용권 기대이익 (D+E)',
                          TOTAL_CP_EXPECTED_PROFIT: '총 CP 소비 매출 (F)'
                        },
                        data: makePreprocessedData(data.channelTotalExpectedProfitList)
                      })}
                      formatYAxis={(value) => {
                        const checkedValue = isFinite(value) ? value : 0;

                        return numberWithCommas(checkedValue);
                      }}
                    />
                  )}
                </>
              )
            },
            {
              id: 'DATA_TABLE',
              title: 'Data Table',
              content: (
                <>
                  {isError ? (
                    <Error />
                  ) : isLoading || isFetching ? (
                    <Loading />
                  ) : (
                    <DataTable
                      data={[...data.channelTotalExpectedProfitList].reverse()}
                      columns={[
                        { header: '날짜', field: 'date' },
                        {
                          header: '구독상품 기대이익KRW (B)',
                          field: 'SUBSCRIPTION_PRODUCT_EXPECTED_PROFIT_KRW',
                          formatFiled: (value) => numberWithCommas(value)
                        },
                        {
                          header: '구독상품 기대이익USD (C)',
                          field: 'SUBSCRIPTION_PRODUCT_EXPECTED_PROFIT_USD',
                          formatFiled: (value) => numberWithCommas(value, 2)
                        },
                        {
                          header: '유료이용권 기대이익KRW (D)',
                          field: 'P_APP_UNLOCK_PRODUCT_EXPECTED_PROFIT_KRW',
                          formatFiled: (value) => numberWithCommas(value)
                        },
                        {
                          header: '유료이용권 기대이익USD (E)',
                          field: 'P_APP_UNLOCK_PRODUCT_EXPECTED_PROFIT_USD',
                          formatFiled: (value) => numberWithCommas(value, 2)
                        },
                        {
                          header: '총 CP 소비 매출 (F)',
                          field: 'TOTAL_CP_EXPECTED_PROFIT',
                          formatFiled: (value) => numberWithCommas(value)
                        },
                        {
                          header: '총 기대이익KRW (G = B+D)',
                          field: 'TOTAL_EXPECTED_PROFIT_KRW',
                          formatFiled: (value) => numberWithCommas(value)
                        },
                        {
                          header: '총 기대이익USD (H = C+E)',
                          field: 'TOTAL_EXPECTED_PROFIT_USD',
                          formatFiled: (value) => numberWithCommas(value, 2)
                        },
                        {
                          header: '추정 총 기대이익 (G + H*환율)',
                          field: 'ESTIMATED_TOTAL_EXPECTED_PROFIT',
                          formatFiled: (value) => numberWithCommas(value)
                        }
                      ]}
                    />
                  )}
                </>
              )
            }
          ]}
        />
      </Card.Body>
    </Card>
  );
}
